import { useState, useEffect } from 'react';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Checkbox } from '@paulownia/shared/components/ui/checkbox';
import type { ServingEstimate } from '@paulownia/shared/types';

const DEFAULT_DISCLAIMER = 'Poids ajusté selon disponibilité';
const DEFAULT_GRAMS_PER_PERSON = 200;

interface WeightEstimatedFieldsProps {
  price?: number;
  servingEstimate?: ServingEstimate;
  disclaimer?: string;
  onPriceChange: (price: number | undefined) => void;
  onServingEstimateChange: (estimate: ServingEstimate | undefined) => void;
  onDisclaimerChange: (disclaimer: string | undefined) => void;
}

export function WeightEstimatedFields({
  price,
  servingEstimate,
  disclaimer,
  onPriceChange,
  onServingEstimateChange,
  onDisclaimerChange,
}: WeightEstimatedFieldsProps) {
  const [priceInput, setPriceInput] = useState(price ? String(price) : '');
  const [gramsInput, setGramsInput] = useState(
    servingEstimate?.gramsPerPerson ? String(servingEstimate.gramsPerPerson) : String(DEFAULT_GRAMS_PER_PERSON)
  );

  // Initialize servingEstimate with default value if not set
  useEffect(() => {
    if (!servingEstimate?.gramsPerPerson) {
      onServingEstimateChange({ gramsPerPerson: DEFAULT_GRAMS_PER_PERSON });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gramsPerPerson = servingEstimate?.gramsPerPerson || DEFAULT_GRAMS_PER_PERSON;

  // Calculate example prices for 1, 2, 4 persons
  const examplePersons = [1, 2, 4];
  const examplePrices = price && gramsPerPerson
    ? examplePersons.map(persons => ({
        persons,
        weight: (gramsPerPerson * persons) / 1000,
        price: price * (gramsPerPerson * persons) / 1000,
      }))
    : [];

  return (
    <div className="space-y-4">
      {/* Price per kg */}
      <div className="space-y-2">
        <Label htmlFor="price-kg">Prix au kilo (€/kg) *</Label>
        <Input
          id="price-kg"
          type="text"
          inputMode="decimal"
          value={priceInput}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d*[.,]?\d{0,2}$/.test(val)) {
              setPriceInput(val);
              const normalized = val.replace(',', '.');
              const parsed = parseFloat(normalized);
              onPriceChange(val === '' ? undefined : (isNaN(parsed) ? undefined : parsed));
            }
          }}
          placeholder="ex: 32.00"
        />
      </div>

      {/* Grams per person */}
      <div className="space-y-2">
        <Label htmlFor="grams-person">Grammes par personne *</Label>
        <div className="flex items-center gap-2">
          <Input
            id="grams-person"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={gramsInput}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                setGramsInput(val);
                const parsed = parseInt(val, 10);
                if (!isNaN(parsed) && parsed > 0) {
                  onServingEstimateChange({ gramsPerPerson: parsed });
                }
              }
            }}
            placeholder="ex: 200"
            className="w-32"
          />
          <span className="text-gray-500 text-sm">g/personne</span>
        </div>
        <p className="text-xs text-gray-500">
          Le client choisira le nombre de personnes lors de sa commande.
        </p>
      </div>

      {/* Example prices */}
      {examplePrices.length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800 mb-2">Exemples de prix :</p>
          <div className="space-y-1">
            {examplePrices.map(({ persons, weight, price: estimatedPrice }) => (
              <p key={persons} className="text-sm text-blue-700">
                {persons} {persons === 1 ? 'personne' : 'personnes'} : {weight.toFixed(2)}kg ≈ {estimatedPrice.toFixed(2)}€
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          id="disclaimer"
          checked={!!disclaimer}
          onCheckedChange={(checked) => {
            onDisclaimerChange(checked ? DEFAULT_DISCLAIMER : undefined);
          }}
        />
        <div className="space-y-1">
          <Label htmlFor="disclaimer" className="cursor-pointer font-normal">
            Afficher "{DEFAULT_DISCLAIMER}"
          </Label>
          <p className="text-xs text-gray-500">
            Ce message sera affiché sous le prix pour informer le client.
          </p>
        </div>
      </div>
    </div>
  );
}
